import logger, { logError, logToServer } from 'middleware/logger';
import { hasOutOfStockCV } from 'helpers/CheckoutUtils';

export const ERROR_WEBLAB_TRIGGER_NON_200 = 'ERROR_WEBLAB_TRIGGER_NON_200';
export const ERROR_AKITA_ESTIMATE_NON_200 = 'ERROR_AKITA_ESTIMATE_NON_200';
export const ERROR_CANNOT_CONFIRM_PURCHASE_OOS = 'ERROR_CANNOT_CONFIRM_PURCHASE_OOS';
export const ERROR_CANNOT_CONFIRM_PURCHASE_OTHER = 'ERROR_CANNOT_CONFIRM_PURCHASE_OTHER';
export const ERROR_EMPTY_CART = 'ERROR_EMPTY_CART';
export const ERROR_NOT_AUTHENTICATED = 'ERROR_NOT_AUTHENTICATED';
export const ERROR_QUANTITY_CHANGE_REQUEST_VALIDATION = 'ERROR_QUANTITY_CHANGE_REQUEST_VALIDATION';
export const ERROR_REQUEST_VALIDATION = 'ERROR_REQUEST_VALIDATION';
export const ERROR_PURCHASE_NOT_FOUND = 'ERROR_PURCHASE_NOT_FOUND';
export const ERROR_INVALID_GIFT_OPTIONS = 'ERROR_INVALID_GIFT_OPTIONS';
export const CHECKOUT_GIFT_OPTIONS_ERROR = 'checkout.input.invalid';
export const CHECKOUT_QUANTITY_CHANGE_ERROR = 'quantity.invalid';
export const CHECKOUT_EMPTY_CART_ERROR = 'checkout.cart.empty';
export const CHECKOUT_CV_EXISTS_ON_FINALIZE_ERROR = 'constraint.violations.exist';
export const CHECKOUT_PURCHASE_NOT_FOUND = 'purchase.not.found';
export const REDEEMABLE_REWARDS_NOT_FOUND = 'REDEEMABLE_REWARDS_NOT_FOUND';
export const CHECKOUT_EDIT_INACTIVE_ADDRESS = 'address.inactive.exception';
export const ERROR_EDIT_INACTIVE_ADDRESS = 'ERROR_EDIT_INACTIVE_ADDRESS';
/**
 * Simple class representing an error that occurred while performing an API request.
 */
export function FetchError(url, status, statusText, id, extraInformation = '') {
  this.url = url;
  this.status = status;
  this.statusText = statusText;
  this.id = id;
  this.extraInformation = extraInformation;
  this.detailedMessage = `API Fetch Error ${status} ${statusText} ${url}`;
}
FetchError.prototype = new Error();

/**
 * fetchErrorMiddleware - inspects fetch response for error codes and throws an error if the response >= 400. Converts response to JSON if successful.
 *
 * @return {Object}  the parsed JSON response
 */
export function fetchErrorMiddleware(response) {
  if (response.status >= 400) {
    throw new FetchError(response.url, response.status, response.statusText);
  }
  return response.json();
}

/**
 * generates a response handler similar to fetchErrorMiddleware, however it will not throw fetch errors for error codes included in allowedResponseCodes
 * @param  {Array}  [allowedResponseCodes=[]] the integer response codes (above 400) that should also be considered legal
 * @return {function}                           The function to pass to a fetch .then
 */
export function fetchErrorMiddlewareAllowedErrors(allowedResponseCodes = []) {
  return response => {
    if (response.status >= 400 && allowedResponseCodes.indexOf(response.status) === -1) {
      throw new FetchError(response.url, response.status, response.statusText);
    }
    return response.json();
  };
}

/**
 * generates a response handler similar to fetchErrorMiddleware, however it will not throw fetch errors for error codes included in allowedResponseCodes, will log all errors to server even if not failing
 * @param  {Array}  [allowedResponseCodes=[]] the integer response codes (above 400) that should also be considered legal
 * @return {function}                           The function to pass to a fetch .then
 */
export function fetchErrorMiddlewareAllowedErrorsWithServerLogging(allowedResponseCodes = [], prefix = 'API') {
  return response => {
    if (response.status >= 400 && allowedResponseCodes.indexOf(response.status) === -1) {
      logToServer(`[${prefix}]: Error code ${response.status} from call ${response.url}`);
      throw new FetchError(response.url, response.status, response.statusText);
    }

    return response.json();
  };
}

export function fetchErrorMiddlewareAllowAllErrorsWithServerLogging(prefix = 'API') {
  return response => {
    if (response.status >= 400) {
      logToServer(`[${prefix}]: Error code ${response.status} from call ${response.url}`);
    }
    return response.json();
  };
}

/**
 * fetchErrorMiddlewareMaybeJson - inspects fetch response for error codes and
 * throws an error if the response >= 400. Converts response to JSON if
 * successful and there is body text returned. If there is no body text, returns null.
 *
 * @return {type}  description
 */
export function fetchErrorMiddlewareMaybeJson(response) {
  if (response.status >= 400) {
    throw new FetchError(response.url, response.status, response.statusText);
  }
  return response.text().then(text => {
    if (text) {
      return JSON.parse(text);
    } else {
      return null;
    }
  });
}

/**
 * fetchErrorMiddleware - inspects fetch response for error codes and throws an error if the response >= 500. In the case of a 400 Converts response to JSON if successful.
 *
 * @return {type}  description
 */
export function fetchAllowNotFoundErrorMiddleware(response) {
  if (response.status >= 500) {
    throw new FetchError(response.url, response.status, response.statusText);
  } else if (response.status === 404) {
    return null;
  }
  return response.json();
}

/**
 * Middleware for handling mafia responses which indicate the user is not
 * authenticated. This middleware will redirect the user to auth. Throws
 * a FetchError when auth is required.
 *
 * @param  {object}   response  Fetch repsonse
 * @return {function}           Fetch Middleware
 */

export const authError = new Set([
  'purchase.access.forbidden',
  'customer.auth.required',
  'invalid.atmain',
  'Access is denied',
  'auth.invalid',
  'Full authentication is required to access this resource',
  'Customer not recognized'
]);

export const fetchApiNotAuthenticatedMiddleware = response => {
  if (response.status !== 403) {
    return response;
  }
  return response.json().then(json => {
    if (authError.has(json.id) || authError.has(json.error) || authError.has(json.message)) {
      throw new FetchError(response.url, response.status, response.statusText, ERROR_NOT_AUTHENTICATED);
    } else {
      return response;
    }
  });
};

export const fetchMafiaValidationErrorMiddleware = response => {
  if (response.status !== 400) {
    return response;
  }

  return response.json().then(json => {
    let hasValidationError = false;

    switch (true) {
      case !json:
        break;
      case json.id === 'address.validation.exception':
      case json.id === 'validation.exception':
      case json.id === 'input.invalid':
        hasValidationError = true;
        break;
    }

    if (hasValidationError) {
      throw new FetchError(response.url, response.status, response.statusText, ERROR_REQUEST_VALIDATION, json.extraInformation);
    }

    return response;
  });
};

/**
 * fetchCannotConfirmPurchaseMiddleware - inspects fetch response (converted to json at this point in the flow)
 * to determine if purchase can't be finalized due to CV present
 *
 * @return {type}  description
 */
export function fetchCannotConfirmPurchaseMiddleware(response) {
  const hasCV = response.statusCode === 200 && response.id === CHECKOUT_CV_EXISTS_ON_FINALIZE_ERROR;

  if (hasCV) {
    const errorType = hasOutOfStockCV(response.extraInformation) ? ERROR_CANNOT_CONFIRM_PURCHASE_OOS : ERROR_CANNOT_CONFIRM_PURCHASE_OTHER;
    throw new FetchError(response.url, response.statusCode, response.statusText, errorType, response.extraInformation);
  }

  return response;
}

/**
 * fetchPurchaseNotFoundErrorMiddleware - inspects fetch response for error codes and throws an error the purchase could not be found
 *
 * @return {type}  description
 */
export function fetchPurchaseNotFoundErrorMiddleware(response) {
  if (response.status !== 404) {
    return response;
  }

  return response.json().then(json => {
    const hasPurchaseNotFoundError = json && (json.id === 'purchase.not.found');
    if (hasPurchaseNotFoundError) {
      throw new FetchError(response.url, response.status, response.statusText, ERROR_PURCHASE_NOT_FOUND, json.description);
    }

    return response;
  });
}

/**
 * fetchPixelErrorMiddleware - if error calling the /pixel endpoint, or it doesn't have the expected response, just log to screen.
 *
 * @return {type}  description
 */
export function fetchPixelErrorMiddleware(response) {
  if (response.status >= 500) {
    logger('Error calling /pixel endpoint, not firing pixel server');
    return null;
  } else if (response.status === 404) {
    logger('Unable to locate /pixel endpoint, not firing pixel server');
    return null;
  }

  return response.json();
}

/**
 * fetchMartyPixelErrorMiddleware - if error calling the /martypixel endpoint, or it doesn't have the expected response, just log to screen.
 *
 * @return {type}  description
 */
export function fetchMartyPixelErrorMiddleware(response) {
  if (response.status >= 500) {
    logError('Error calling /martypixel endpoint, not firing pixel server');
    return null;
  } else if (response.status === 404) {
    logError('Unable to locate /martypixel endpoint, not firing pixel server');
    return null;
  }

  return response;
}

/**
 * fetchCheckout400ErrorMiddleware - inspects fetch response for error codes and throws an error for 400 status codes
 *
 * @return {type}  description
 */
export function fetchCheckout400ErrorMiddleware(response) {
  if (response.status !== 400) {
    return response;
  }

  return response.json().then(json => {
    if (!json) {
      return response;
    }

    switch (json.id) {
      case CHECKOUT_GIFT_OPTIONS_ERROR: {
        // gift options error
        throw new FetchError(response.url, response.status, response.statusText, ERROR_INVALID_GIFT_OPTIONS, json.description);
      }
      case CHECKOUT_QUANTITY_CHANGE_ERROR: {
        // quantity change error
        throw new FetchError(response.url, response.status, response.statusText, ERROR_QUANTITY_CHANGE_REQUEST_VALIDATION, json.description);
      }
      case CHECKOUT_EMPTY_CART_ERROR: {
        // empty cart error
        throw new FetchError(response.url, response.status, response.statusText, ERROR_EMPTY_CART);
      }
      default: {
        // generic 400 error
        throw new FetchError(response.url, response.status, response.statusText);
      }
    }
  });
}

/**
 * fetchNon200Middleware - If not a 200, then throw error
 *
 * @return {type}  description
 */
export function fetchNon200Middleware(response) {
  if (response.status === 200) {
    return response.text();
  } else {
    throw new FetchError(response.url, response.status, response.statusText);
  }
}
